import { Modal, Alert } from '@pmi/dsm-react';
import { Formik, FormikErrors, FormikProps } from 'formik';
import Overlay from '../../../../../shared/components/Overlay';
import { useEffect, useRef, useState } from 'react';
import { Product } from '../../../models';
import styles from '../../../productcard.module.scss';
import { courseTypeDescription } from '../../../helpers';
import { ProfileModel, RedemptionProfile } from './models';
import { useUpdateProfileMutation } from '../apiSlice';
import useSchemaValidator from '../../../../../shared/hooks/useSchemaValidator';
import {
  Address,
  PhoneNumber,
} from '../../../../../shared/models/ProfileModel';
import PhoneNumberForm from '../../../../../shared/components/PhoneNumberForm';
import AddressForm from './AddressForm';
import CategoryLabel from '../../../../../shared/components/CategoryLabel';
import {
  reportError,
  toastWithAnalytics,
} from '../../../../../shared/analytics';

interface ProfileUpdateModalProps {
  product: Product;
  onCancel: () => void;
  onSubmit: () => void;
  onSuccess: () => void;
  reset: () => void;
  isError: boolean;
  phoneInputNeeded: boolean;
  addressInputNeeded: boolean;
  redemptionProfile: RedemptionProfile;
}

export default function ProfileUpdateModal({
  product,
  onCancel,
  onSuccess,
  onSubmit,
  reset,
  isError,
  phoneInputNeeded,
  addressInputNeeded,
  redemptionProfile,
}: ProfileUpdateModalProps) {
  const formikRef = useRef<FormikProps<ProfileModel>>(undefined);
  const [isCountryRestricted, setIsCountryRestricted] = useState(false);
  const errorMessage = 'There was an error placing the order';
  useEffect(() => {
    if (isError) reportError(errorMessage);
  }, [isError]);

  const { level, totalPDUs } = product;
  const initialValues: ProfileModel = {
    phoneNumber: {
      countryId: null,
      extension: '',
      number: '',
    },
    address: { country: '', state: '' },
  };

  const [updateProfile, { isLoading: updateProfileLoading }] =
    useUpdateProfileMutation();
  const { validatePhoneNumber, validateAddress } = useSchemaValidator();
  const validateProfile = (values: ProfileModel) => {
    const errors: FormikErrors<ProfileModel> = {};
    try {
      if (phoneInputNeeded) {
        const result = validatePhoneNumber(
          {
            ...values.phoneNumber,
            type: 1, // for preventing validation to fail
            countryId: values.phoneNumber?.countryId
              ? Number(values.phoneNumber?.countryId)
              : null,
          },
          redemptionProfile?.phoneNumbers?._embedded?.schema,
        );
        if (!result.valid) {
          errors.phoneNumber = result.errors;
        }
      }

      if (addressInputNeeded) {
        const result = validateAddress(
          { ...values.address },
          redemptionProfile?.addresses?._embedded?.schema,
        );
        if (!result.valid) {
          errors.address = result.errors;
        }
      }
    } catch (error) {
      toastWithAnalytics('Unexpected Error while validating data', 'danger');
    }
    setTimeout(() => {
      const invalids = document.querySelectorAll(
        '.mdlProfileUpdate .is-invalid',
      );
      if (invalids.length > 0) {
        invalids.item(0).scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return errors;
  };

  const onProfileFormSubmit = async (values: ProfileModel) => {
    onSubmit();
    let phoneNumber: PhoneNumber = null;
    if (phoneInputNeeded) {
      phoneNumber = values.phoneNumber;
    }
    let address: Address = null;
    if (addressInputNeeded) {
      address = values.address;
    }
    await updateProfile({
      address,
      phoneNumber,
    }).unwrap();
    onSuccess();
  };

  const onConfirmClick = async () => {
    try {
      await formikRef.current.submitForm();
    } catch (error) {
      toastWithAnalytics(
        'There was an unexpected error while updating profile details',
        'danger',
      );
    }
  };

  const onCountryRestricted = (isRestricted: boolean): void => {
    setIsCountryRestricted(isRestricted);
    const modalFooter: HTMLDivElement = document.querySelector(
      `.${styles.productCard} .modal-footer`,
    );
    if (modalFooter) {
      const elId = `${styles.productCard}-country-restricted`;
      const elDiv = document.querySelector(`[data-id="${elId}"]`);
      if (elDiv) {
        elDiv.remove();
      }
      if (isRestricted) {
        const alert = document.createElement('div');
        alert.setAttribute('data-id', elId);
        alert.classList.add('d-block', 'mb-3', 'w-100', 'text-end');
        alert.innerHTML =
          '<p class="d-block m-0 text-danger">We cannot process enrollments for residents of China. Please select another country.</p>';
        modalFooter.prepend(alert);
      }
    }
  };

  return (
    <Modal
      show
      headline="Confirm Your Selection"
      customClassName={`d-flex flex-column ${styles.productCard}`}
      variant="input"
      onClose={onCancel}
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancel,
        },
        {
          titleText: 'Confirm and Proceed',
          variant: 'primary',
          onClick: onConfirmClick,
          disabled: formikRef.current?.isSubmitting || isCountryRestricted,
        },
      ]}
    >
      <div className="d-flex flex-column mdlProfileUpdate">
        <div>
          <CategoryLabel type={product.type} className="mb-3 d-inline-flex" />
        </div>
        <h4
          className="d-inline-block mb-2"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: product.name }}
        />
        <div className="label mb-4">
          {level && <span data-testid="course-level">{level}</span>}
          {level && totalPDUs && ' | '}
          {totalPDUs && `PDUs: ${totalPDUs}`}
        </div>
        <hr />
        <h3>Additional Information Required</h3>
        <p>
          To access this {courseTypeDescription(product.type)} please add the
          following information, which will be saved to your PMI Profile.
        </p>
        <Formik
          initialValues={initialValues}
          onSubmit={onProfileFormSubmit}
          validate={validateProfile}
          validateOnBlur={false}
          validateOnChange={false}
          innerRef={formikRef}
        >
          <>
            {phoneInputNeeded && (
              <PhoneNumberForm
                countries={
                  redemptionProfile?.phoneNumbers?._embedded?.countries
                }
              />
            )}

            {addressInputNeeded && (
              <AddressForm
                countries={redemptionProfile?.addresses?._embedded?.countries}
                onCountryRestricted={onCountryRestricted}
              />
            )}
          </>
        </Formik>

        {isError && (
          <div className="mb-4">
            <Alert
              show
              alertText={errorMessage}
              variant="danger"
              dismissButtonLabel="Dismiss"
              onDismissButtonClick={reset}
            />
          </div>
        )}
        <Overlay
          variant="PMIOverlay"
          global
          animated
          fade
          iconColor="brand-dark"
          overlayColor="black"
          overlayOpacity={1}
          show={updateProfileLoading}
        />
      </div>
    </Modal>
  );
}
